import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Autocomplete,
  IconButton,
  CircularProgress
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get } from "lodash";

import Panel from "../../../components/Panel";
import ToggleInputDate from "../../../components/ToggleADBS";
import { bsDateField } from "../../../models/Accounts";
import { defaultTrainingOptions } from "../../../models/Client";
import { isISODateString } from "../../../helpers/validators";
import { t } from "../../../components/translate";
import { useAppDispatch } from "../../../store/hooks";
import { notificationAdd } from "../../../actions/notification";
import useCurrentResourceCentre from "../../../hooks/useCurrentResourceCentre";
import { ApiResponse } from "../../../interfaces/Api";
import { Training, TrainingSignatory } from "../../../interfaces/TrainingInterface";
import { usePostTraining, usePatchTraining } from "../../../hooks/query/useTraining";

export interface TrainingCreateEditProps {
  clientId?: number;
  training?: Training;
  onClose: () => void;
  afterSave?: (savedTraining: Training) => void;
}

interface TrainingFormValues {
  name: string;
  certificateNumber: string;
  duration: {
    value: string;
    unit: string;
  };
  startDate: string;
  endDate: string;
  issueDate: string;
  signatories: TrainingSignatory[];
}

const TrainingCreateEdit: React.FC<TrainingCreateEditProps> = ({
  clientId,
  training,
  onClose,
  afterSave
}) => {
  const dispatch = useAppDispatch();
  const resourceCentre = useCurrentResourceCentre();
  const [saving, setSaving] = useState(false);

  const { mutateAsync: postTrainingMutate } = usePostTraining();
  const { mutateAsync: patchTrainingMutate } = usePatchTraining();

  const isEditMode = Boolean(training && training.id);
  const effectiveClientId = clientId || training?.clientId;

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<TrainingFormValues>({
    defaultValues: {
      name: training?.name || "",
      certificateNumber: training?.certificateNumber || "",
      duration: {
        value: training?.duration?.value ? String(training.duration.value) : "",
        unit: training?.duration?.unit || "Hours"
      },
      startDate: training?.startDate ? String(training.startDate).slice(0, 10) : "",
      endDate: training?.endDate ? String(training.endDate).slice(0, 10) : "",
      issueDate: training?.issueDate ? String(training.issueDate).slice(0, 10) : "",
      signatories: training?.signatories?.length ? training.signatories : []
    },
    mode: "onBlur"
  });

  const {
    fields: signatoryFields,
    append: appendSignatory,
    remove: removeSignatory
  } = useFieldArray({
    control,
    name: "signatories"
  });

  const onSubmit = async (values: TrainingFormValues) => {
    if (!effectiveClientId) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          variant: "error",
          message: "Client ID is required.",
          autoTimeout: true
        })
      );
      return;
    }

    setSaving(true);
    try {
      const payload: Partial<Training> = {
        clientId: effectiveClientId,
        resourceCentreId: training?.resourceCentreId || resourceCentre?.id,
        name: values.name.trim(),
        certificateNumber: values.certificateNumber?.trim() || undefined,
        duration: values.duration?.value
          ? {
              value: values.duration.value.trim(),
              unit: values.duration.unit || "Hours"
            }
          : undefined,
        startDate: values.startDate || undefined,
        endDate: values.endDate || undefined,
        issueDate: values.issueDate || undefined,
        signatories: values.signatories || []
      };

      let response: ApiResponse<Training>;
      if (isEditMode && training?.id) {
        response = await patchTrainingMutate({ id: training.id, data: payload });
      } else {
        response = await postTrainingMutate(payload);
      }

      if (afterSave && response?.data) {
        afterSave(response.data);
      }
      onClose();
    } catch (err: unknown) {
      const errorObj = err as { response?: { data?: { message?: string } }; message?: string };
      const errMsg =
        errorObj?.response?.data?.message ||
        errorObj?.message ||
        (isEditMode ? "Failed to update training" : "Failed to create training");

      if (errMsg.toLowerCase().includes("certificate number")) {
        setError("certificateNumber", {
          type: "manual",
          message: errMsg
        });
      }
    } finally {
      setSaving(false);
    }
  };

  let saveButtonText = "Save";
  if (saving) {
    saveButtonText = "Saving...";
  } else if (isEditMode) {
    saveButtonText = "Update";
  }

  return (
    <Panel
      title={isEditMode ? "Edit Training" : "Add Training"}
      onClose={onClose}
      footer={
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            width: "100%"
          }}
        >
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
            data-testmation="saveTrainingButton"
          >
            {saveButtonText}
          </Button>
        </Box>
      }
    >
      <Grid container columnSpacing={2}>
        {/* Training Name */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="name"
            control={control}
            rules={{
              required: "Training name is required"
            }}
            render={({ field }) => (
              <Autocomplete
                freeSolo
                options={defaultTrainingOptions}
                value={field.value || ""}
                onChange={(_, newValue) => {
                  field.onChange(newValue || "");
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === "input" || reason === "clear") {
                    field.onChange(newInputValue || "");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    fullWidth
                    required
                    variant="outlined"
                    label={t("CreateClient.TrainingName")}
                    placeholder="Select or type training name"
                    data-testmation="trainingNameInput"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={!!errors.name}
                    helperText={errors.name?.message || " "}
                  />
                )}
              />
            )}
          />
        </Grid>

        {/* Certificate Number */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="certificateNumber"
            control={control}
            render={({ field }) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...field}
                fullWidth
                variant="outlined"
                label={t("CreateClient.CertificateNumber")}
                placeholder="e.g. CERT-12345"
                data-testmation="certificateNumberInput"
                slotProps={{ inputLabel: { shrink: true } }}
                error={!!errors.certificateNumber}
                helperText={errors.certificateNumber?.message || " "}
              />
            )}
          />
        </Grid>

        {/* Duration */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="duration.value"
            control={control}
            render={({ field }) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...field}
                fullWidth
                variant="outlined"
                label={t("CreateClient.Duration")}
                placeholder="e.g. 40"
                data-testmation="durationValueInput"
                slotProps={{ inputLabel: { shrink: true } }}
                error={!!get(errors, "duration.value.message")}
                helperText={(get(errors, "duration.value.message") as string) || " "}
              />
            )}
          />
        </Grid>

        {/* Duration Unit */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="duration.unit"
            control={control}
            render={({ field }) => (
              <Autocomplete
                freeSolo
                options={["Hours", "Days", "Weeks", "Months"]}
                value={field.value || "Hours"}
                onChange={(_, newValue) => {
                  field.onChange(newValue || "Hours");
                }}
                onInputChange={(_, newInputValue, reason) => {
                  if (reason === "input" || reason === "clear") {
                    field.onChange(newInputValue || "");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...params}
                    fullWidth
                    variant="outlined"
                    label={t("CreateClient.DurationUnit")}
                    placeholder="Hours"
                    data-testmation="durationUnitInput"
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={!!get(errors, "duration.unit.message")}
                    helperText={(get(errors, "duration.unit.message") as string) || " "}
                  />
                )}
              />
            )}
          />
        </Grid>

        {/* Dates: Start Date, End Date, Issue Date */}
        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            name="startDate"
            control={control}
            rules={{
              validate: (value) =>
                (value ? !!isISODateString(value) : true) || "Please enter valid date"
            }}
            render={({ field }) => (
              <ToggleInputDate
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...field}
                data-testmation="trainingStartDate"
                field={{
                  ...bsDateField,
                  key: "startDate",
                  showAgeField: false
                }}
                data={{
                  startDate: field.value || ""
                }}
                changeDate={(value: string) => {
                  field.onChange(value);
                }}
                triggerInternalStateUpdateOnDateChange
                label="startDate"
                error={!!errors.startDate}
                helperText={errors.startDate?.message || ""}
                isFocused={() => ({})}
                isBlurred={() => ({})}
                showAgeField={false}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            name="endDate"
            control={control}
            rules={{
              validate: (value) =>
                (value ? !!isISODateString(value) : true) || "Please enter valid date"
            }}
            render={({ field }) => (
              <ToggleInputDate
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...field}
                data-testmation="trainingEndDate"
                field={{
                  ...bsDateField,
                  key: "endDate",
                  showAgeField: false
                }}
                data={{
                  endDate: field.value || ""
                }}
                changeDate={(value: string) => {
                  field.onChange(value);
                }}
                triggerInternalStateUpdateOnDateChange
                label="endDate"
                error={!!errors.endDate}
                helperText={errors.endDate?.message || ""}
                isFocused={() => ({})}
                isBlurred={() => ({})}
                showAgeField={false}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <Controller
            name="issueDate"
            control={control}
            rules={{
              validate: (value) =>
                (value ? !!isISODateString(value) : true) || "Please enter valid date"
            }}
            render={({ field }) => (
              <ToggleInputDate
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...field}
                data-testmation="trainingIssueDate"
                field={{
                  ...bsDateField,
                  key: "issueDate",
                  showAgeField: false
                }}
                data={{
                  issueDate: field.value || ""
                }}
                changeDate={(value: string) => {
                  field.onChange(value);
                }}
                triggerInternalStateUpdateOnDateChange
                label="certificateIssueDate"
                error={!!errors.issueDate}
                helperText={errors.issueDate?.message || ""}
                isFocused={() => ({})}
                isBlurred={() => ({})}
                showAgeField={false}
              />
            )}
          />
        </Grid>

        {/* Signatories Section */}
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              my: 2,
              py: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderTop: 1,
              borderBottom: 1,
              borderColor: "divider"
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t("CreateClient.Signatories")}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() =>
                appendSignatory({
                  order: signatoryFields.length + 1,
                  role: "",
                  name: ""
                })
              }
              variant="outlined"
              sx={{ textTransform: "none", fontSize: "0.75rem" }}
              data-testmation="addSignatoryButton"
            >
              {t("CreateClient.AddSignatory")}
            </Button>
          </Box>

          {signatoryFields.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {signatoryFields.map((item, sigIndex) => (
                <Grid container spacing={1} key={item.id} alignItems="center">
                  <Grid size={{ xs: 12, sm: 2 }}>
                    <Controller
                      name={`signatories.${sigIndex}.order` as const}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          fullWidth
                          type="number"
                          variant="outlined"
                          label={t("CreateClient.SignatoryOrder")}
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Controller
                      name={`signatories.${sigIndex}.role` as const}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          fullWidth
                          variant="outlined"
                          label={t("CreateClient.SignatoryRole")}
                          placeholder="e.g. Instructor, Director"
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Controller
                      name={`signatories.${sigIndex}.name` as const}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          // eslint-disable-next-line react/jsx-props-no-spreading
                          {...field}
                          fullWidth
                          variant="outlined"
                          label={t("CreateClient.SignatoryName")}
                          placeholder="e.g. Dr. John Doe"
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 2 }}>
                    <IconButton
                      color="error"
                      onClick={() => removeSignatory(sigIndex)}
                      title={t("CreateClient.RemoveTraining")}
                      size="small"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                </Grid>
              ))}
            </Box>
          )}
        </Grid>
      </Grid>
    </Panel>
  );
};

export default TrainingCreateEdit;
